'use client';
import useHash from 'hooks/useHash';
import { ShopifyCollection } from 'lib/shopify/types';
import { useEffect, useState } from 'react';
import { cn } from 'utils/cn';

interface CollectionNavigationProps {
  collections: ShopifyCollection[];
  scrollContainerRef?: React.RefObject<HTMLDivElement>;
  useContainerScroll?: boolean;
  style?: 'default' | 'dropdown';
}

export function CollectionNavigation({ 
  collections, 
  scrollContainerRef, 
  useContainerScroll = false,
  style = 'default'
}: CollectionNavigationProps) {
  const hash = useHash();
  const [activeHandle, setActiveHandle] = useState(hash || collections[0]?.handle);

  useEffect(() => {
    // Skip scroll detection for dropdown style
    if (!scrollContainerRef?.current || (window.innerWidth <= 768 && style === 'dropdown')) return;

    const handleScroll = () => {
      const navbarHeight = parseInt(getComputedStyle(document.documentElement).getPropertyValue('--navbar-height'));
      
      // Find which section is most visible in the viewport
      let mostVisibleSection = '';
      let maxVisibility = 0;

      collections.forEach(collection => {
        const element = document.getElementById(collection.handle);
        if (!element) return;

        const rect = element.getBoundingClientRect();
        const elementTop = rect.top - navbarHeight;
        const elementBottom = rect.bottom - navbarHeight;
        const windowHeight = window.innerHeight;

        // Calculate how much of the element is visible
        const visibleHeight = Math.min(elementBottom, windowHeight) - Math.max(elementTop, 0);
        const visibility = Math.max(0, visibleHeight / rect.height);

        if (visibility > maxVisibility) {
          maxVisibility = visibility;
          mostVisibleSection = collection.handle;
        }
      });

      if (mostVisibleSection && mostVisibleSection !== activeHandle) {
        console.log('Setting active handle to:', mostVisibleSection);
        setActiveHandle(mostVisibleSection);
      }
    };

    const scrollElement = useContainerScroll ? scrollContainerRef.current : window;
    scrollElement.addEventListener('scroll', handleScroll);
    
    // Initial check
    handleScroll();

    return () => {
      scrollElement.removeEventListener('scroll', handleScroll);
    };
  }, [collections, scrollContainerRef, useContainerScroll, activeHandle, style]);

  useEffect(() => {
    // Set initial state for the first collection on mobile
    if (window.innerWidth <= 768 && style === 'dropdown' && collections[0]?.handle) {
      const firstElement = document.getElementById(collections[0].handle);
      if (firstElement) {
        firstElement.style.maxHeight = '2000px';
        firstElement.style.opacity = '1';
      }
    }
  }, [collections]);

  const handleClick = (e: React.MouseEvent<HTMLAnchorElement>, handle: string) => {
    e.preventDefault();
    
    const element = document.getElementById(handle);
    if (!element) return;

    if (window.innerWidth <= 768 && style === 'dropdown') { // Mobile behavior
        if (activeHandle === handle) {
            // If clicking the active handle, close it
            element.style.maxHeight = '0px';
            element.style.opacity = '0';
            setActiveHandle(''); // Clear active handle
        } else {
            // Hide all collection content first
            collections.forEach(col => {
                const colElement = document.getElementById(col.handle);
                if (colElement) {
                    colElement.style.maxHeight = '0px';
                    colElement.style.opacity = '0';
                }
            });
            // Show selected collection content
            element.style.maxHeight = '2000px';
            element.style.opacity = '1';
            setActiveHandle(handle);
        }
    } else { // Desktop scroll behavior
      if (useContainerScroll && scrollContainerRef?.current) {
        const navbarHeight = parseInt(getComputedStyle(document.documentElement).getPropertyValue('--navbar-height'));
        const elementPosition = element.offsetTop - navbarHeight;
        scrollContainerRef.current.scrollTo({
          top: elementPosition,
          behavior: 'smooth'
        });
      } else {
        element.scrollIntoView({ behavior: 'smooth' });
      }
      setActiveHandle(handle);
    }
  };

  return (
    <div className="flex w-full md:w-fit mx-auto px-4 flex-col items-center justify-start gap-6 py-2 lg:flex-row rounded-xl bg-white">
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-3 gap-4 xl:flex lg:flex-row lg:gap-4 ">
        {collections.map((collection, index) => (
          <h2 key={collection.handle} className='text-base font-normal'>
            <a
              href={`#${collection.handle}`}
              onClick={(e) => handleClick(e, collection.handle)}
              className={cn('whitespace-nowrap scroll-mt-[var(--navbar-height)] text-base font-normal hover:text-fp-green text-center', {
                'font-medium text-fp-green': collection.handle === activeHandle
              })}
            >
              {collection.title}
            </a>
          </h2>
        ))}
      </div>
    </div>
  );
}
